import type { Activity, ScheduleModel } from '../types/schedule';

/**
 * Derived analytics over a ScheduleModel: earned value, float distribution,
 * WBS rollups, baseline variance and critical-path extraction.
 *
 * Everything here is pure and memo-friendly — the dashboard recomputes on
 * filter changes, so nothing may mutate the model it is given.
 */

const MS_PER_DAY = 86_400_000;

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function monthLabel(date: Date): string {
  return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

/** Inclusive list of month starts spanning two dates, capped for safety. */
export function monthsBetween(from: Date, to: Date, cap = 480): Date[] {
  const months: Date[] = [];
  const cursor = startOfMonth(from);
  const end = startOfMonth(to);
  while (cursor <= end && months.length < cap) {
    months.push(new Date(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

export function calendarDaysBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / MS_PER_DAY);
}

/* ── Project finish ────────────────────────────────────────────────────── */

/**
 * Identify the activity that represents project completion.
 *
 * "The one with the latest date" is not good enough: a dangling activity with
 * no logic often finishes later than the real completion milestone, and it
 * would then anchor CPLI and the critical-path test to a node that drives
 * nothing. Prefer a terminal node (no successors, but fed by predecessors),
 * and among those prefer a finish milestone.
 */
export function findProjectFinishActivity(schedule: ScheduleModel): Activity | null {
  const real = schedule.activities.filter(
    (a) => a.finish && a.type !== 'WBS Summary' && a.type !== 'Level of Effort'
  );
  if (real.length === 0) return null;

  const latest = (pool: Activity[]) =>
    pool.reduce((best, a) => (a.finish! > best.finish! ? a : best), pool[0]);

  const terminal = real.filter(
    (a) => a.successors.length === 0 && a.predecessors.length > 0
  );

  if (terminal.length > 0) {
    const milestones = terminal.filter((a) => a.isMilestone);
    return latest(milestones.length > 0 ? milestones : terminal);
  }

  return latest(real);
}

/* ── Earned value ──────────────────────────────────────────────────────── */

export interface CurvePoint {
  date: Date;
  label: string;
  /** Budgeted cost of work scheduled, cumulative. */
  planned: number;
  /** Budgeted cost of work performed, cumulative. Stops at the data date. */
  earned: number | null;
  /** Actual cost of work performed, cumulative. Stops at the data date. */
  actual: number | null;
}

export interface EvmSummary {
  /** False when the file carries no cost data, in which case the curve is
   *  activity-count based and the indices are schedule-only. */
  hasCost: boolean;
  unit: 'cost' | 'activities';
  budgetAtCompletion: number;
  plannedValue: number;
  earnedValue: number;
  actualCost: number;
  scheduleVariance: number;
  costVariance: number;
  /** Earned / planned. Null when there is nothing planned yet. */
  spi: number | null;
  /** Earned / actual. Null without cost data. */
  cpi: number | null;
  estimateAtCompletion: number | null;
  varianceAtCompletion: number | null;
  curve: CurvePoint[];
}

/**
 * Spread a value evenly across the months an activity spans. P6 can hold a
 * true time-phased spread, but it is not present in every export, so a linear
 * distribution across the activity's own dates is the honest approximation.
 */
function spreadAcrossMonths(
  start: Date | null,
  finish: Date | null,
  amount: number,
  into: Map<number, number>,
  clampTo?: Date | null
): void {
  if (!amount || !start) return;
  const effectiveFinish = finish && finish > start ? finish : start;
  const cappedFinish =
    clampTo && effectiveFinish > clampTo ? clampTo : effectiveFinish;
  if (clampTo && start > clampTo) return;

  const months = monthsBetween(start, cappedFinish);
  if (months.length === 0) return;

  // Weight by the share of the activity's span that falls in each month, so a
  // long activity does not get an artificial step at month boundaries.
  const totalDays = Math.max(1, calendarDaysBetween(start, cappedFinish));
  let assigned = 0;

  months.forEach((month, index) => {
    const monthStart = index === 0 ? start : month;
    const nextMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1);
    const monthEnd = nextMonth > cappedFinish ? cappedFinish : nextMonth;
    const days = Math.max(0, calendarDaysBetween(monthStart, monthEnd));

    const share =
      index === months.length - 1
        ? amount - assigned // absorb rounding in the final bucket
        : (days / totalDays) * amount;

    assigned += share;
    const key = month.getTime();
    into.set(key, (into.get(key) ?? 0) + share);
  });
}

export function computeEvm(
  schedule: ScheduleModel,
  baseline?: ScheduleModel
): EvmSummary {
  const costSource = baseline ?? schedule;
  const hasCost = costSource.activities.some((a) => a.budgetCost > 0);
  const dataDate = schedule.dataDate;

  const plannedByMonth = new Map<number, number>();
  const earnedByMonth = new Map<number, number>();
  const actualByMonth = new Map<number, number>();

  // Planned value follows the baseline where we have one — that is the whole
  // point of a baseline — and the current plan otherwise.
  for (const activity of costSource.activities) {
    const amount = hasCost ? activity.budgetCost : 1;
    if (!amount) continue;
    spreadAcrossMonths(
      activity.targetStart ?? activity.start,
      activity.targetFinish ?? activity.finish,
      amount,
      plannedByMonth
    );
  }

  for (const activity of schedule.activities) {
    const budget = hasCost ? activity.budgetCost : 1;
    const earned = budget * (activity.percentComplete / 100);
    const actual = hasCost ? activity.actualCost : activity.isComplete ? 1 : 0;

    spreadAcrossMonths(
      activity.actualStart ?? activity.start,
      activity.actualFinish ?? activity.finish,
      earned,
      earnedByMonth,
      dataDate
    );
    spreadAcrossMonths(
      activity.actualStart ?? activity.start,
      activity.actualFinish ?? activity.finish,
      actual,
      actualByMonth,
      dataDate
    );
  }

  const allKeys = new Set<number>([
    ...plannedByMonth.keys(),
    ...earnedByMonth.keys(),
    ...actualByMonth.keys(),
  ]);
  const sortedKeys = [...allKeys].sort((a, b) => a - b);

  let cumulativePlanned = 0;
  let cumulativeEarned = 0;
  let cumulativeActual = 0;

  const curve: CurvePoint[] = sortedKeys.map((key) => {
    const date = new Date(key);
    cumulativePlanned += plannedByMonth.get(key) ?? 0;
    cumulativeEarned += earnedByMonth.get(key) ?? 0;
    cumulativeActual += actualByMonth.get(key) ?? 0;

    // Earned and actual are only meaningful up to the data date; beyond it the
    // line must stop rather than flatten, which would read as "no progress".
    const beyondDataDate = !!dataDate && date > startOfMonth(dataDate);

    return {
      date,
      label: monthLabel(date),
      planned: cumulativePlanned,
      earned: beyondDataDate ? null : cumulativeEarned,
      actual: beyondDataDate ? null : cumulativeActual,
    };
  });

  const budgetAtCompletion = cumulativePlanned;
  const pointsToDataDate = dataDate
    ? curve.filter((p) => p.date <= startOfMonth(dataDate))
    : [];
  const plannedValue = dataDate
    ? (pointsToDataDate[pointsToDataDate.length - 1]?.planned ?? 0)
    : cumulativePlanned;
  const earnedValue = cumulativeEarned;
  const actualCost = cumulativeActual;

  const spi = plannedValue > 0 ? earnedValue / plannedValue : null;
  const cpi = hasCost && actualCost > 0 ? earnedValue / actualCost : null;
  const estimateAtCompletion =
    cpi && cpi > 0 ? budgetAtCompletion / cpi : null;

  return {
    hasCost,
    unit: hasCost ? 'cost' : 'activities',
    budgetAtCompletion,
    plannedValue,
    earnedValue,
    actualCost,
    scheduleVariance: earnedValue - plannedValue,
    costVariance: earnedValue - actualCost,
    spi,
    cpi,
    estimateAtCompletion,
    varianceAtCompletion:
      estimateAtCompletion !== null ? budgetAtCompletion - estimateAtCompletion : null,
    curve,
  };
}

/* ── Float distribution ────────────────────────────────────────────────── */

export interface FloatBin {
  label: string;
  min: number;
  max: number;
  count: number;
  activityIds: string[];
  /** Bins beyond the 44-day DCMA threshold, or below zero. */
  severity: 'negative' | 'critical' | 'healthy' | 'excess';
}

const FLOAT_BINS: { label: string; min: number; max: number; severity: FloatBin['severity'] }[] = [
  // The upper bound is exclusive of zero: an activity with exactly zero float
  // is critical, not behind, and belongs in the next bin.
  { label: '< 0', min: -Infinity, max: -0.0001, severity: 'negative' },
  { label: '0', min: -0.0001, max: 0.999, severity: 'critical' },
  { label: '1–5', min: 1, max: 5.999, severity: 'healthy' },
  { label: '6–10', min: 6, max: 10.999, severity: 'healthy' },
  { label: '11–20', min: 11, max: 20.999, severity: 'healthy' },
  { label: '21–44', min: 21, max: 44.999, severity: 'healthy' },
  { label: '45–100', min: 45, max: 100.999, severity: 'excess' },
  { label: '> 100', min: 101, max: Infinity, severity: 'excess' },
];

export function computeFloatDistribution(activities: Activity[]): FloatBin[] {
  const bins: FloatBin[] = FLOAT_BINS.map((b) => ({ ...b, count: 0, activityIds: [] }));

  for (const activity of activities) {
    if (activity.isComplete) continue;
    const float = activity.totalFloatDays;
    const bin = bins.find((b) => float >= b.min && float <= b.max);
    if (bin) {
      bin.count++;
      bin.activityIds.push(activity.id);
    }
  }

  return bins;
}

/* ── Baseline variance ─────────────────────────────────────────────────── */

export interface VarianceEntry {
  activityId: string;
  code: string;
  name: string;
  baselineFinish: Date | null;
  currentFinish: Date | null;
  /** Positive = later than baseline (slip). */
  slipDays: number;
  isCritical: boolean;
  status: string;
}

export interface BaselineComparison {
  entries: VarianceEntry[];
  added: { code: string; name: string; activityId: string }[];
  removed: { code: string; name: string }[];
  /** Slip on the project finish milestone, in calendar days. */
  projectSlipDays: number | null;
  meanSlipDays: number;
  slippedCount: number;
  improvedCount: number;
}

export function compareToBaseline(
  schedule: ScheduleModel,
  baseline: ScheduleModel
): BaselineComparison {
  const baselineByCode = new Map(baseline.activities.map((a) => [a.code, a]));
  const currentByCode = new Map(schedule.activities.map((a) => [a.code, a]));

  const entries: VarianceEntry[] = [];
  const added: BaselineComparison['added'] = [];

  for (const activity of schedule.activities) {
    const baselineActivity = baselineByCode.get(activity.code);
    if (!baselineActivity) {
      added.push({ code: activity.code, name: activity.name, activityId: activity.id });
      continue;
    }
    if (!activity.finish || !baselineActivity.finish) continue;

    entries.push({
      activityId: activity.id,
      code: activity.code,
      name: activity.name,
      baselineFinish: baselineActivity.finish,
      currentFinish: activity.finish,
      slipDays: calendarDaysBetween(baselineActivity.finish, activity.finish),
      isCritical: activity.isCritical,
      status: activity.status,
    });
  }

  const removed = baseline.activities
    .filter((a) => !currentByCode.has(a.code))
    .map((a) => ({ code: a.code, name: a.name }));

  // Measure the movement of the *same* activity in both files. Comparing the
  // latest finish in each is wrong as soon as scope changes: dropping the
  // activity that happened to finish last reads as a huge slip.
  const currentFinishActivity = findProjectFinishActivity(schedule);
  const baselineCounterpart = currentFinishActivity
    ? baselineByCode.get(currentFinishActivity.code)
    : undefined;

  const projectSlipDays =
    currentFinishActivity?.finish && baselineCounterpart?.finish
      ? calendarDaysBetween(baselineCounterpart.finish, currentFinishActivity.finish)
      : schedule.finish && baseline.finish
        ? calendarDaysBetween(baseline.finish, schedule.finish)
        : null;

  const slipped = entries.filter((e) => e.slipDays > 0);
  const improved = entries.filter((e) => e.slipDays < 0);

  return {
    entries: entries.sort((a, b) => Math.abs(b.slipDays) - Math.abs(a.slipDays)),
    added,
    removed,
    projectSlipDays,
    meanSlipDays: entries.length
      ? entries.reduce((sum, e) => sum + e.slipDays, 0) / entries.length
      : 0,
    slippedCount: slipped.length,
    improvedCount: improved.length,
  };
}

/* ── WBS rollup ────────────────────────────────────────────────────────── */

export interface WbsRollupNode {
  id: string;
  name: string;
  code: string;
  parentId: string | null;
  depth: number;
  activityCount: number;
  activityIds: string[];
  budgetCost: number;
  actualCost: number;
  durationDays: number;
  /** Mean total float across incomplete activities in this branch. */
  meanFloatDays: number;
  criticalCount: number;
  percentComplete: number;
  children: WbsRollupNode[];
}

export function computeWbsRollup(
  schedule: ScheduleModel,
  activities: Activity[]
): WbsRollupNode[] {
  const byWbs = new Map<string, Activity[]>();
  for (const activity of activities) {
    const key = activity.wbsId ?? '__unassigned__';
    const list = byWbs.get(key);
    if (list) list.push(activity);
    else byWbs.set(key, [activity]);
  }

  const nodes = new Map<string, WbsRollupNode>();
  for (const wbs of schedule.wbs) {
    nodes.set(wbs.id, {
      id: wbs.id,
      name: wbs.name,
      code: wbs.code,
      parentId: wbs.parentId,
      depth: 0,
      activityCount: 0,
      activityIds: [],
      budgetCost: 0,
      actualCost: 0,
      durationDays: 0,
      meanFloatDays: 0,
      criticalCount: 0,
      percentComplete: 0,
      children: [],
    });
  }

  if (byWbs.has('__unassigned__')) {
    nodes.set('__unassigned__', {
      id: '__unassigned__',
      name: 'Unassigned',
      code: '',
      parentId: null,
      depth: 0,
      activityCount: 0,
      activityIds: [],
      budgetCost: 0,
      actualCost: 0,
      durationDays: 0,
      meanFloatDays: 0,
      criticalCount: 0,
      percentComplete: 0,
      children: [],
    });
  }

  // Direct assignment first, then roll values up through the ancestry.
  for (const [wbsId, list] of byWbs) {
    let node = nodes.get(wbsId);
    if (!node) continue;

    const floats: number[] = [];
    for (const activity of list) {
      let cursor: WbsRollupNode | undefined = node;
      const seen = new Set<string>();
      while (cursor && !seen.has(cursor.id)) {
        seen.add(cursor.id);
        cursor.activityCount++;
        cursor.activityIds.push(activity.id);
        cursor.budgetCost += activity.budgetCost;
        cursor.actualCost += activity.actualCost;
        cursor.durationDays += activity.originalDurationDays;
        if (activity.isCritical) cursor.criticalCount++;
        cursor = cursor.parentId ? nodes.get(cursor.parentId) : undefined;
      }
      if (!activity.isComplete) floats.push(activity.totalFloatDays);
    }

    node = nodes.get(wbsId)!;
    node.meanFloatDays = floats.length
      ? floats.reduce((a, b) => a + b, 0) / floats.length
      : 0;
  }

  for (const node of nodes.values()) {
    const ids = new Set(node.activityIds);
    const branch = activities.filter((a) => ids.has(a.id));
    node.percentComplete = branch.length
      ? branch.reduce((sum, a) => sum + a.percentComplete, 0) / branch.length
      : 0;
  }

  // Link children and compute depth.
  const roots: WbsRollupNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  const assignDepth = (node: WbsRollupNode, depth: number) => {
    node.depth = depth;
    node.children.forEach((child) => assignDepth(child, depth + 1));
  };
  roots.forEach((root) => assignDepth(root, 0));

  return roots.filter((r) => r.activityCount > 0);
}

/* ── Critical path ─────────────────────────────────────────────────────── */

export interface CriticalPathResult {
  /** Critical activities in date order. */
  chain: Activity[];
  /** Relationships between consecutive chain members. */
  links: { from: string; to: string; type: string; lagDays: number }[];
  lengthDays: number;
  /** True when P6's own longest-path flag was available and used. */
  usedDrivingFlag: boolean;
}

export function computeCriticalPath(schedule: ScheduleModel): CriticalPathResult {
  // Prefer P6's longest-path flag when the file was scheduled with it on;
  // otherwise fall back to zero-or-negative total float.
  const drivingCount = schedule.activities.filter((a) => a.isDrivingPath).length;
  const usedDrivingFlag = drivingCount > 0;

  const chain = schedule.activities
    .filter((a) => (usedDrivingFlag ? a.isDrivingPath : a.isCritical))
    .filter((a) => a.type !== 'WBS Summary' && a.type !== 'Level of Effort')
    .sort((a, b) => {
      const aStart = a.start?.getTime() ?? 0;
      const bStart = b.start?.getTime() ?? 0;
      return aStart - bStart;
    });

  const chainIds = new Set(chain.map((a) => a.id));
  const links = schedule.relationships
    .filter((r) => chainIds.has(r.predecessorId) && chainIds.has(r.successorId))
    .map((r) => ({
      from: r.predecessorId,
      to: r.successorId,
      type: r.type,
      lagDays: r.lagDays,
    }));

  const lengthDays = chain.reduce((sum, a) => sum + a.originalDurationDays, 0);

  return { chain, links, lengthDays, usedDrivingFlag };
}

/* ── Headline metrics ──────────────────────────────────────────────────── */

export interface HeadlineMetrics {
  totalActivities: number;
  completeCount: number;
  inProgressCount: number;
  notStartedCount: number;
  criticalCount: number;
  milestoneCount: number;
  percentComplete: number;
  totalFloatMedian: number;
  negativeFloatCount: number;
  remainingDurationDays: number;
}

export function computeHeadlines(activities: Activity[]): HeadlineMetrics {
  const incomplete = activities.filter((a) => !a.isComplete);
  const floats = incomplete.map((a) => a.totalFloatDays).sort((a, b) => a - b);

  return {
    totalActivities: activities.length,
    completeCount: activities.filter((a) => a.isComplete).length,
    inProgressCount: activities.filter((a) => a.isInProgress).length,
    notStartedCount: activities.filter((a) => a.isNotStarted).length,
    criticalCount: activities.filter((a) => a.isCritical && !a.isComplete).length,
    milestoneCount: activities.filter((a) => a.isMilestone).length,
    percentComplete: activities.length
      ? activities.reduce((sum, a) => sum + a.percentComplete, 0) / activities.length
      : 0,
    totalFloatMedian: floats.length ? floats[Math.floor(floats.length / 2)] : 0,
    negativeFloatCount: incomplete.filter((a) => a.totalFloatDays < 0).length,
    remainingDurationDays: incomplete.reduce(
      (sum, a) => sum + a.remainingDurationDays,
      0
    ),
  };
}
